/**
 * Every enum the API answers with.
 *
 * ── THIS MODULE IMPORTS NOTHING, ON PURPOSE ────────────────────────────────
 * Enums are what domain modules share, so leaving each one in the domain that
 * happened to need it first is how you get an import cycle. `audits.ts` needed
 * `severitySchema` from `misc.ts`, `misc.ts` needed `auditStatusSchema` from
 * `audits.ts`, and at runtime whichever loaded second read a `const` that had
 * not been initialised yet — "Cannot access 'auditStatusSchema' before
 * initialization".
 *
 * `tsc` cannot warn about this: a cycle between types is legal and resolves
 * fine. Only the emitted module graph breaks, and only when it runs.
 *
 * So: if it is a `z.enum`, it belongs here, and this file's import list stays
 * empty. That keeps it a leaf of the graph, which no cycle can pass through.
 */
import { z } from 'zod';

/* ── people ─────────────────────────────────────────────────────────────── */

export const roleSchema = z.enum(['ADMIN', 'AUDITOR', 'DATA_ENTRY']);
export const userStatusSchema = z.enum(['ACTIVE', 'DISABLED']);

/* ── files ──────────────────────────────────────────────────────────────── */

export const fileStatusSchema = z.enum([
	'UPLOADING',
	'UPLOADED',
	'MAPPING_REQUIRED',
	'MAPPING_CONFIRMED',
	/* The mapping has been applied and the normalised Parquet written, with no
     rules run yet. Getting here is the minutes-long job; classifying from here
     is seconds — so the two needed telling apart on screen. */
	'NORMALISED',
	'PROCESSING',
	'READY',
	'FAILED',
	'EXCLUDED',
	'AWAITING_REPLACEMENT',
	'REPLACED',
]);

/** What the client says a file is. Declared, not detected. */
/* The file's kind, set by the app from its classification counts (DB enum
   "FileKind"). */
export const declaredTypeSchema = z.enum([
	'SALES',
	'RETURNS',
	'BOTH',
	'UNKNOWN',
]);

/* ── mapping ────────────────────────────────────────────────────────────── */

export const canonicalTypeSchema = z.enum([
	'text',
	'date',
	'money',
	'decimal',
	'integer',
]);
export const fieldTierSchema = z.enum([
	'CRITICAL',
	'STRUCTURAL',
	'DESCRIPTIVE',
]);
export const mappingBasisSchema = z.enum([
	'EXACT',
	'ALIAS',
	'SUGGESTED',
	'UNMAPPED',
	'CONFIRMED',
	'REMEMBERED',
]);

/* ── processing and analysis ────────────────────────────────────────────── */

export const runStatusSchema = z.enum([
	'QUEUED',
	'RUNNING',
	'COMPLETED',
	'FAILED',
]);

/* ── audits ─────────────────────────────────────────────────────────────── */

export const auditStatusSchema = z.enum([
	'DRAFT',
	'SETUP',
	'IN_PROGRESS',
	'UNDER_REVIEW',
	'CLOSED',
	'RESTATED',
]);

export const exceptionStatusSchema = z.enum([
	'OPEN',
	'ASSIGNED',
	'IN_REVIEW',
	'ACCEPTED',
	'CORRECTED',
	'ESCALATED',
	'CLOSED',
]);

export const reportStatusSchema = z.enum([
	'DRAFT',
	'UNDER_REVIEW',
	'APPROVED',
	'ISSUED',
	'SUPERSEDED',
]);

export const severitySchema = z.enum([
	'BLOCKING',
	'EXCEPTION',
	'WARNING',
	'INFO',
]);

/**
 * What a row IS, per the firm's classification rules.
 *
 * Plural, and it matters: the vocabulary was normalised across the database
 * and the API (`backend/src/config/classifications.ts`), rewriting 303,304
 * live rows. The old frontend list stayed singular, so the rule form went on
 * POSTing `SALE` to an API that had stopped accepting it — every
 * classification rule written from the UI failed with a 400. Keep this in step
 * with `CLASSIFICATIONS` on the server.
 */
export const classificationSchema = z.enum([
	'SALES',
	'RETURNS',
	'ADJUSTMENT',
	'EXCLUDED',
	'UNKNOWN',
]);

export type Role = z.infer<typeof roleSchema>;
export type UserStatus = z.infer<typeof userStatusSchema>;
export type FileStatus = z.infer<typeof fileStatusSchema>;
export type DeclaredType = z.infer<typeof declaredTypeSchema>;
export type CanonicalType = z.infer<typeof canonicalTypeSchema>;
export type FieldTier = z.infer<typeof fieldTierSchema>;
export type MappingBasis = z.infer<typeof mappingBasisSchema>;
export type RunStatus = z.infer<typeof runStatusSchema>;
export type AuditStatus = z.infer<typeof auditStatusSchema>;
export type ExceptionStatus = z.infer<typeof exceptionStatusSchema>;
export type ReportStatus = z.infer<typeof reportStatusSchema>;
export type Severity = z.infer<typeof severitySchema>;
export type Classification = z.infer<typeof classificationSchema>;
